import React, {
  createContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import { IStateContentResponse } from 'Interfaces/Umbraco/MedicalCommittees';
import { getStateContent } from 'API/MedicalCommittees';
import { useLoader } from 'Hooks/useLoader';
import { useAppSelector } from 'Hooks';

interface MedicalCommitteesContextType {
  stateContent: IStateContentResponse | null;
  fetchStateContent: (
    stateContentId: string,
    committeeLocationId?: number,
  ) => Promise<void>;
  refreshStateContent: () => Promise<void>;
  setStateContentId: (
    stateContentId: string,
    committeeLocationId?: number,
  ) => void;
  loader: React.ReactNode;
  isLoading: boolean;
}

const MedicalCommitteesContext = createContext<MedicalCommitteesContextType>({
  stateContent: null,
  fetchStateContent: () => Promise.resolve(),
  refreshStateContent: () => Promise.resolve(),
  setStateContentId: () => {},
  loader: <></>,
  isLoading: false,
});

interface MedicalCommitteesProviderProps {
  children: ReactNode;
}

export const MedicalCommitteesProvider: React.FC<
  MedicalCommitteesProviderProps
> = ({ children }) => {
  const [stateContent, setStateContent] =
    useState<IStateContentResponse | null>(null);
  const [currentStateContentId, setCurrentStateContentId] = useState<
    string | null
  >(null);
  const [currentCommitteeLocationId, setCurrentCommitteeLocationId] = useState<
    number | null
  >(null);
  const { loader, isLoading, setIsLoading } = useLoader();

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId,
  );

  const fetchStateContent = useCallback(
    async (stateContentId: string, committeeLocationId?: number) => {
      setIsLoading(true);
      setCurrentStateContentId(stateContentId);
      setCurrentCommitteeLocationId(committeeLocationId || null);

      try {
        const content = await getStateContent(
          stateContentId,
          committeeLocationId,
        );
        setStateContent(content || null);
      } catch (err) {
        console.error('Error fetching state content:', err);
        setStateContent(null);
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading],
  );

  const refreshStateContent = useCallback(async () => {
    if (currentStateContentId) {
      await fetchStateContent(
        currentStateContentId,
        currentCommitteeLocationId || undefined,
      );
    }
  }, [currentStateContentId, currentCommitteeLocationId, fetchStateContent]);

  const setStateContentId = useCallback(
    (stateContentId: string, committeeLocationId?: number) => {
      setCurrentStateContentId(stateContentId);
      setCurrentCommitteeLocationId(committeeLocationId || null);
    },
    [],
  );

  useEffect(() => {
    if (currentStateContentId && imposterId) {
      fetchStateContent(
        currentStateContentId,
        currentCommitteeLocationId || undefined,
      );
    }
  }, [
    imposterId,
    currentStateContentId,
    currentCommitteeLocationId,
    fetchStateContent,
  ]);

  const contextValue: MedicalCommitteesContextType = {
    stateContent,
    fetchStateContent,
    refreshStateContent,
    setStateContentId,
    loader,
    isLoading,
  };

  return (
    <MedicalCommitteesContext.Provider value={contextValue}>
      {children}
    </MedicalCommitteesContext.Provider>
  );
};

export { MedicalCommitteesContext };
